import React, { useMemo } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { DAD_PARTNERS_DOCTRINE } from "../../config/dadPartnersDoctrine";
import { getStakeholderLaneAppearance, LANE_STATUS_META } from "../../config/stakeholderLaneConfig";

function DadCard({ item, onOpen }) {
  const appearance = getStakeholderLaneAppearance(item.lane);
  const disablePersonRouteActions = !!item.disable_person_route_actions;
  const proofSignals = [
    item.current_condition_note ? `Condition: ${item.current_condition_note}` : "",
    item.home_proof_signal ? `Home proof: ${item.home_proof_signal}` : "",
    item.weekly_rhythm_signal ? `Rhythm: ${item.weekly_rhythm_signal}` : "",
  ].filter(Boolean);

  return (
    <button
      className={`w-full rounded-2xl border border-slate-200/80 bg-white/90 p-4 text-left shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] ${appearance.cardHoverClass} ${disablePersonRouteActions ? "cursor-not-allowed opacity-70" : ""}`}
      disabled={disablePersonRouteActions}
      onClick={() => onOpen(item.id)}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{item.reason}</p>
        </div>
        {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {item.relationship_stage_label ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.relationship_stage_label}</Badge> : null}
        {item.dominant_pain_area ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.dominant_pain_area}</Badge> : null}
        {item.children_age_label ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.children_age_label}</Badge> : null}
      </div>

      {item.next_move ? (
        <p className={`mt-3 text-xs font-medium uppercase tracking-[0.16em] ${appearance.accentTextClass}`}>
          Next: {item.next_move}
        </p>
      ) : null}
      {item.last_meaningful_movement ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.last_meaningful_movement}</p>
      ) : null}
      {item.latest_contact_summary ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.latest_contact_summary}</p>
      ) : null}
      {item.next_follow_up_summary ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.next_follow_up_summary}</p>
      ) : null}
      {proofSignals.length ? (
        <div className={`mt-3 space-y-1 rounded-xl border p-3 text-xs leading-5 ${appearance.chipClass}`}>
          {proofSignals.map((signal) => (
            <p key={signal}>{signal}</p>
          ))}
        </div>
      ) : null}
      {item.contact_label ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.contact_label}</p>
      ) : null}
    </button>
  );
}

function Section({ children, className = "", description, title }) {
  return (
    <section className={`rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03] ${className}`}>
      <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
        <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{title}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{description}</p>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function DadPartnersLanePage({ lane, laneConfig, onOpen }) {
  const navigate = useNavigate();
  const statusMeta = LANE_STATUS_META[lane.status];
  const appearance = getStakeholderLaneAppearance(lane.id);

  const needsNowItems = useMemo(() => lane.items["needs-now"] || [], [lane.items]);
  const movingItems = useMemo(() => lane.items.moving || [], [lane.items]);
  const watchItems = useMemo(() => lane.items.watch || [], [lane.items]);
  const allDads = useMemo(() => [...needsNowItems, ...movingItems, ...watchItems], [needsNowItems, movingItems, watchItems]);

  const cultureSignals = useMemo(() => {
    const courageVisible = movingItems.length;
    const directKevinNeeds = needsNowItems.filter((item) => item.is_kevin_flagged).length;
    const watchCount = watchItems.length;
    const stageJoinedCount = allDads.filter((item) => item.relationship_stage === "dad_partners_founding_forty_joined").length;

    return [
      {
        label: "Courage / wins visible",
        value: courageVisible,
        note: "Men currently showing momentum instead of just presence.",
      },
      {
        label: "Kevin follow-ups needed",
        value: directKevinNeeds,
        note: "Dads or threads where your personal voice still matters most.",
      },
      {
        label: "Surface or drift risk",
        value: watchCount,
        note: "Men who are present but not yet opening up or moving.",
      },
      {
        label: "Founding 40 dads",
        value: stageJoinedCount,
        note: "Current joined dads who should feel brotherhood becoming real.",
      },
    ];
  }, [allDads, movingItems, needsNowItems, watchItems]);

  const riskNotes = useMemo(() => {
    const cultureMentions = allDads.filter((item) =>
      `${item.reason} ${item.notes}`.toLowerCase().includes("preach") ||
      `${item.reason} ${item.notes}`.toLowerCase().includes("culture")
    ).length;
    const quietAfterHonesty = allDads.filter((item) =>
      `${item.reason} ${item.notes}`.toLowerCase().includes("quiet")
    ).length;
    const noPressureContext = allDads.filter((item) => !item.dominant_pain_area).length;

    return [
      {
        label: "Culture/tone risks visible",
        value: cultureMentions,
      },
      {
        label: "Quiet-after-honesty signals",
        value: quietAfterHonesty,
      },
      {
        label: "Low-context dad records",
        value: noPressureContext,
      },
    ];
  }, [allDads]);

  const doctrineSignals = useMemo(() => {
    const conditionNotes = allDads.filter((item) => item.current_condition_note).length;
    const homeProofSignals = allDads.filter((item) => item.home_proof_signal).length;
    const weeklyRhythmSignals = allDads.filter((item) => item.weekly_rhythm_signal).length;

    return [
      {
        label: "Condition notes",
        value: conditionNotes,
      },
      {
        label: "Home-visible proof",
        value: homeProofSignals,
      },
      {
        label: "Weekly rhythm signals",
        value: weeklyRhythmSignals,
      },
    ];
  }, [allDads]);

  const homeProofItems = useMemo(
    () =>
      allDads.filter(
        (item) =>
          item.current_condition_note ||
          item.home_proof_signal ||
          item.weekly_rhythm_signal
      ),
    [allDads]
  );

  return (
    <div className={`min-h-screen pb-10 ${appearance.pageClass}`}>
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        <div className={`rounded-[30px] border bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:bg-[#081018]/90 ${appearance.shellClass}`}>
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${appearance.headingTextClass}`}>
                Lane Detail
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {lane.title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{lane.promise}</p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {laneConfig.charter}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
              <Button onClick={() => navigate("/community")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Community Home
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3 xl:grid-cols-5">
            <div className="rounded-2xl border border-rose-200/70 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-100">
                Needs Now
              </p>
              <p className="mt-2 text-3xl font-semibold text-rose-950 dark:text-white">
                {lane.counts["needs-now"]}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-100">
                Moving
              </p>
              <p className="mt-2 text-3xl font-semibold text-emerald-950 dark:text-white">
                {lane.counts.moving}
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/80 p-5 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-100">
                Watch
              </p>
              <p className="mt-2 text-3xl font-semibold text-amber-950 dark:text-white">
                {lane.counts.watch}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Courage visible
              </p>
              <p className="mt-2 inline-flex items-center gap-2 text-3xl font-semibold text-slate-950 dark:text-white">
                <ShieldCheck className={`h-6 w-6 ${appearance.accentTextClass}`} />
                {cultureSignals[0].value}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Kevin follow-ups
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-950 dark:text-white">
                {cultureSignals[1].value}
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="Dads or threads where courage, conflict, or culture make direct follow-up important now."
                title="Needs Kevin Now"
              >
                <div className="space-y-3">
                  {needsNowItems.length ? (
                    needsNowItems.map((item) => <DadCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No urgent dads or culture issues are surfaced right now.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Men showing real courage, visible wins, or movement that should be reinforced."
                title="Courage And Wins"
              >
                <div className="space-y-3">
                  {movingItems.length ? (
                    movingItems.map((item) => <DadCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No visible courage or wins are being tracked yet.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Men who are present but still guarded, surface-level, or quietly sliding toward drift."
                title="Watch / Drift"
              >
                <div className="space-y-3">
                  {watchItems.length ? (
                    watchItems.map((item) => <DadCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No obvious drift signals are surfaced right now.
                    </p>
                  )}
                </div>
              </Section>
            </div>

            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="The lane stays anchored in what dads are carrying now, what courage looks like, and whether change is visible at home."
                title="Lane Doctrine"
              >
                <div className="space-y-4">
                  <div className={`rounded-2xl border p-4 ${appearance.panelClass}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Current condition
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">
                      {DAD_PARTNERS_DOCTRINE.currentCondition}
                    </p>
                  </div>
                  <div className={`rounded-2xl border p-4 ${appearance.panelClass}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Home-visible proof
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">
                      {DAD_PARTNERS_DOCTRINE.homeProof}
                    </p>
                  </div>
                  <div className={`rounded-2xl border p-4 ${appearance.panelClass}`}>
                    <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${appearance.headingTextClass}`}>
                      AI Gem voice
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">
                      {DAD_PARTNERS_DOCTRINE.gemWriterVoice.audience}
                    </p>
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Lead with
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {DAD_PARTNERS_DOCTRINE.gemWriterVoice.leadWith.map((value) => (
                          <Badge key={value} variant="outline">
                            {value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Avoid
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {DAD_PARTNERS_DOCTRINE.gemWriterVoice.avoid.map((value) => (
                          <Badge key={value} variant="outline">
                            {value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {DAD_PARTNERS_DOCTRINE.gemWriterVoice.scriptureBoundary}
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {doctrineSignals.map((signal) => (
                      <div
                        className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                        key={signal.label}
                      >
                        <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${appearance.headingTextClass}`}>
                          {signal.label}
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                          {signal.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="A first pass at whether the brotherhood is producing courage, follow-up, and safety."
                title="Culture Health"
              >
                <div className="space-y-3">
                  {cultureSignals.map((signal) => (
                    <div
                      className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                      key={signal.label}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{signal.label}</p>
                        <p className="text-2xl font-semibold text-slate-950 dark:text-white">{signal.value}</p>
                      </div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{signal.note}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Signals that could point to brotherhood softening, becoming preachy, or losing track of men."
                title="Intervention Triggers"
              >
                <div className="space-y-3">
                  {riskNotes.map((risk) => (
                    <div
                      className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                      key={risk.label}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{risk.label}</p>
                        <p className="text-2xl font-semibold text-slate-950 dark:text-white">{risk.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Open any dad card to update current condition, home-visible proof, weekly rhythm, wins, and next move."
                title="Healthy Rhythms"
              >
                <div className="flex flex-wrap gap-2">
                  {DAD_PARTNERS_DOCTRINE.weeklyRhythms.map((rhythm) => (
                    <Badge key={rhythm} variant="outline">
                      {rhythm}
                    </Badge>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  This lane should help you see whether courage is being met with brotherhood, whether quiet men
                  are disappearing, and whether the culture still sounds like support instead of correction.
                </p>
              </Section>

              <Section
                description="Dad-specific evidence entered in the drawer, separate from generic admin status."
                title="Home-Visible Movement"
              >
                <div className="space-y-3">
                  {homeProofItems.length ? (
                    homeProofItems.map((item) => <DadCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No home-visible proof has been recorded yet.
                    </p>
                  )}
                </div>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
